import arLearning from '../../../src/i18n/locales/redesign/learning.ar.json';
import enLearning from '../../../src/i18n/locales/redesign/learning.en.json';
import arFamily from '../../../src/i18n/locales/redesign/family.ar.json';
import enFamily from '../../../src/i18n/locales/redesign/family.en.json';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * The redesign's learning strings (`learn.*`), read from the SAME fragment the
 * mobile app merges in src/i18n/bundles.ts.
 *
 * The web LanguageContext still reads the base files only, and switching it to
 * the full bundle would change other pages' strings under their tests. Until
 * the fragments are merged back into ar.json/en.json this hook serves the
 * learning fragment and falls back to the base `t` for everything else. The
 * waiting room and call read a few keys (`waiting.*`, `call.micOn`, …) that
 * only the family fragment holds; those are used only where the base has no
 * such key, so no base string another page relies on is overridden.
 */
const FRAGMENTS = { ar: arLearning, en: enLearning } as const;
const EXTRA = { ar: arFamily, en: enFamily } as const;

function lookup(tree: unknown, key: string): string | null {
  let value: unknown = tree;
  for (const part of key.split('.')) {
    if (value && typeof value === 'object' && part in (value as Record<string, unknown>)) {
      value = (value as Record<string, unknown>)[part];
    } else {
      return null;
    }
  }
  return typeof value === 'string' ? value : null;
}

export function useLearnText() {
  const { t, language } = useLanguage();
  return (key: string, params?: Record<string, string | number>) => {
    let found = lookup(FRAGMENTS[language], key);
    if (found === null) {
      const base = t(key, params);
      if (base !== key) return base;
      found = lookup(EXTRA[language], key);
      if (found === null) return key;
    }
    return found.replace(/\{\{(\w+)\}\}/g, (_, name: string) => (
      params && params[name] !== undefined ? String(params[name]) : ''
    ));
  };
}
